import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { BRAND } from '../brand';

export default function CartDrawer() {
  const { items, updateQty, remove, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-brand-cream">
        <SheetHeader className="p-6 border-b border-brand-gold/20 bg-white">
          <SheetTitle className="font-serif-brand text-2xl text-brand-dark">Your Cart ({items.length})</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 text-brand-green/70">
              <ShoppingBag size={40} className="text-brand-gold mb-3" />
              <p className="text-sm">Your cart is empty.</p>
              <Button className="btn-gold mt-4" onClick={() => { setOpen(false); navigate('/shop'); }}>Start Shopping</Button>
            </div>
          )}
          {items.map((item) => (
            <div key={item.product_id} className="bg-white rounded-xl p-3 flex gap-3 border border-brand-gold/10">
              <img loading="lazy" src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-cover bg-brand-ivory" />
              <div className="flex-1">
                <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="font-medium text-brand-dark text-sm line-clamp-2">
                  {item.name}
                </Link>
                <div className="text-sm text-brand-green/80 mt-1">₹{item.price.toFixed(0)}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="inline-flex items-center border border-brand-gold/30 rounded-full">
                    <button className="px-2 py-1 text-brand-dark" onClick={() => updateQty(item.product_id, item.quantity - 1)}>
                      <Minus size={12} />
                    </button>
                    <span className="px-2 text-sm min-w-[24px] text-center">{item.quantity}</span>
                    <button className="px-2 py-1 text-brand-dark" onClick={() => updateQty(item.product_id, item.quantity + 1)}>
                      <Plus size={12} />
                    </button>
                  </div>
                  <button className="text-brand-green/70 hover:text-red-600" onClick={() => remove(item.product_id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="p-5 border-t border-brand-gold/20 bg-white space-y-3">
            <div className="flex justify-between text-sm text-brand-green/80">
              <span>Subtotal</span>
              <span className="font-medium text-brand-dark">₹{subtotal.toFixed(0)}</span>
            </div>
            <div className="text-xs text-brand-green/70">
              {subtotal >= BRAND.free_shipping_threshold
                ? '✓ You qualify for FREE shipping!'
                : `Add ₹${(BRAND.free_shipping_threshold - subtotal).toFixed(0)} more for free shipping`}
            </div>
            <Button className="btn-gold w-full h-11 rounded-full" onClick={() => { setOpen(false); navigate('/checkout'); }}>
              Checkout • ₹{subtotal.toFixed(0)}
            </Button>
            <Button variant="outline" className="w-full rounded-full border-brand-gold text-brand-dark" onClick={() => { setOpen(false); navigate('/cart'); }}>
              View Cart
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
